import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { cn } from "@/lib/utils";
import { DollarSign, TrendingUp, Users, RefreshCw } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

interface FinancialData {
  monthlyRevenue: number;
  customerGrowth: number;
  renewalRate: number;
  activeSubscriptions: number;
  revenueHistory: {
    month: string;
    revenue: number;
    subscriptions: number;
  }[];
}

interface FinancialMetricsCardProps {
  data: FinancialData;
  className?: string;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border bg-background p-3 shadow-sm">
        <p className="text-sm font-medium mb-1">{label}</p>
        {payload.map((entry: any) => (
          <p key={entry.name} className="text-sm">
            <span className={cn(
              "inline-block w-3 h-3 rounded-full mr-2",
              entry.name === 'revenue' ? 'bg-primary' : 'bg-primary/50'
            )} />
            {entry.name === 'revenue' ? 'Gelir' : 'Abonelik'}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function FinancialMetricsCard({ data, className }: FinancialMetricsCardProps) {
  const metrics = [
    {
      name: 'Aylık Gelir',
      value: `₺${data.monthlyRevenue.toLocaleString()}`,
      icon: DollarSign,
      change: '+12%'
    },
    {
      name: 'Müşteri Büyümesi',
      value: `${data.customerGrowth}%`,
      icon: TrendingUp,
      change: '+5%'
    },
    {
      name: 'Yenileme Oranı',
      value: `${data.renewalRate}%`,
      icon: RefreshCw,
      change: '+3%'
    },
    {
      name: 'Aktif Abonelik',
      value: data.activeSubscriptions,
      icon: Users,
      change: '+8%'
    }
  ];

  return (
    <Card className={cn("col-span-full lg:col-span-8", className)}>
      <CardHeader>
        <CardTitle className="text-xl font-bold">Finansal Metrikler</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((metric) => (
            <div
              key={metric.name}
              className="p-4 rounded-lg bg-muted/50 space-y-2"
            >
              <div className="flex items-center justify-between">
                <metric.icon className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-medium text-green-600">
                  {metric.change}
                </span>
              </div>
              <div>
                <p className="text-2xl font-bold">{metric.value}</p>
                <p className="text-xs text-muted-foreground">{metric.name}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.revenueHistory}>
              <XAxis
                dataKey="month"
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `₺${value}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="revenue"
                fill="currentColor"
                radius={[4, 4, 0, 0]}
                className="fill-primary"
              />
              <Bar
                dataKey="subscriptions"
                fill="currentColor"
                radius={[4, 4, 0, 0]}
                className="fill-primary/50"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
